import classNames from 'classnames'
import React from 'react'

import { renderTypeIcon } from '../../../../AttachmentCard'

import type { CardVariant } from './MediaShell'

export interface CardBodyProps {
  variant: CardVariant
  title?: string
  placeholderTitle?: string
  mimeType: string
  detail?: string
  statusBadge?: React.ReactNode
  action?: React.ReactNode
  /** Overrides the auto-detected type icon (used by Gallery to swap in an `Images` icon). */
  icon?: React.ReactNode
  /** Optional control rendered on the right of the title/status block (e.g. Composer edit pencil). */
  trailingAction?: React.ReactNode
}

/**
 * Title + status row layout shared by Composer and `LockedAttachment.Media`.
 * Layout matches the Figma `Container > Labels` group (16px h-padding, 12px v-padding,
 * 4px gap between title and status row, 4px gap inside status row).
 */
const CardBody: React.FC<CardBodyProps> = ({
  variant,
  title,
  placeholderTitle = 'Attachment title',
  mimeType,
  detail,
  statusBadge,
  action,
  icon,
  trailingAction,
}) => {
  const isDark = variant === 'dark'
  const displayTitle = title || placeholderTitle
  const titleDimmed = !title

  const typeIcon =
    icon ??
    renderTypeIcon(mimeType, {
      className: classNames(
        'size-5 shrink-0',
        isDark ? 'text-white/55' : 'text-black/55'
      ),
      weight: 'regular',
    })

  return (
    <div className="px-4 py-3">
      <div className="flex items-end gap-3">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          {displayTitle.trim() !== '' && (
            <p
              className={classNames(
                'truncate text-base font-medium leading-6',
                {
                  'text-black/90': !isDark && !titleDimmed,
                  'text-black/30': !isDark && titleDimmed,
                  'text-white/30': isDark && titleDimmed,
                  'text-white': isDark && !titleDimmed,
                }
              )}
            >
              {displayTitle}
            </p>
          )}

          <div className="flex flex-wrap items-center gap-1">
            {typeIcon}

            {detail != null && detail !== '' && (
              <span
                className={classNames(
                  'text-xs font-medium',
                  isDark ? 'text-white/55' : 'text-black/55'
                )}
              >
                {detail}
              </span>
            )}

            {statusBadge}
          </div>
        </div>

        {trailingAction && <div className="shrink-0">{trailingAction}</div>}
      </div>

      {action}
    </div>
  )
}

export default CardBody
